import React from 'react';

import {
	EVENT_CATEGORY,
	encodeBase64,
	enumToArray,
	getColor,
	getEventExtraDataParsed,
	getNebulaMobileDeepLink,
	hexAlpha,
	includesSome,
	isDefined,
	isEmpty,
	makeTime,
	mapDefined,
} from '@nfctron/nexus-core';
import { UiButton, useFormat } from '@nfctron/nexus-react';

import { AppShell, useMantineTheme } from '@mantine/core';
import * as Sentry from '@sentry/nextjs';
import { QueryClient, dehydrate, useQueryClient } from '@tanstack/react-query';
import { formatISODuration, intervalToDuration } from 'date-fns';
import { LockIcon } from 'lucide-react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type GetStaticPaths } from 'next/types';
import { type Event as EventJsonLd, type Offer } from 'schema-dts';

import { useTranslate } from '@root/config/src/lib/hooks/useTranslation';

import { ApiError, createPassApi, matchesApiError } from '@root/features/api-client/api-client';
import { type GetEventEventGroupsResultDto } from '@root/features/api-client/generated/pass.api';
import { useSessionStore } from '@root/features/authentication/stores/sessionStore';
import { Header } from '@root/features/layout/components/Header/Header';

import { useEntryQueue } from '@root/cart/_resources/hooks/useEntryQueue';
import { EventLayoutAtom } from '@root/cart/cart/_resources/atoms/EventLayout.atom';

import { JsonLd, createJsonLdOrganizer, createJsonLdPlace } from '@root/lib/components/JsonLd/JsonLd';
import { MultiView } from '@root/lib/components/MultiView/MultiView';
import { APP_I18N_NAMESPACE, APP_LOCALE } from '@root/lib/constants/common';
import { eventKindEnum } from '@root/lib/constants/eventKind';
import { eventTypeEnum } from '@root/lib/constants/eventType';
import { languageEnumTickets } from '@root/lib/constants/language';
import { paymentMethodEnumTickets } from '@root/lib/constants/paymentMethod';
import { constructCompanyEntity } from '@root/lib/entities/company.entity';
import { constructEventColorConfig, constructEventCurrency } from '@root/lib/entities/event.entity';
import { constructOrganizerEntity } from '@root/lib/entities/organizer.entity';
import { useDebug } from '@root/lib/hooks/useDebug';
import { useOrgIntegratorConfig } from '@root/lib/hooks/useOrgIntegrators';
import {
	type PageComponent,
	type PageParams,
	type PageQuery,
	type PageWithCommonProps,
	getStaticPathsWithLocales,
	wrapStaticPropsWithI18n,
} from '@root/lib/modules/next/next.module';

import { getEventPageOverride, getEventTrackingOverride } from '../_resources/hooks/getDataOverride';
import { useEventOgImageParams } from '../_resources/hooks/useEventOgImageParams';

import { EventCartPageContent } from './_resources/atoms/EventCart.atom';
import { FooterAtom } from './_resources/atoms/Footer.atom';
import { EventCartPageContext, type EventCartPageContextProps, type UseInitCartPageData } from './_resources/components/CartContext';
import {
	CartStoreProvider,
	type CartStoreTicketTypesSlice,
	cartStoreQueryKeys,
	cartStoreSelectors,
	categoriesApiQueryFn,
	createTicketTypeConfig,
	getCartTicketSaleState,
	getMaximusProductsApiQueryFn,
	useCartStore,
	useMaximusProductsApi,
	useTicketTypesApi,
	venueApiQueryFn,
} from './_resources/stores/cartStore';

export const CartInner: React.FC<
	React.PropsWithChildren<{
		eventData: EventCartPageProps['eventData'];
		eventGroupsData: EventCartPageProps['eventGroupsData'];
	}>
> = (props) => {
	const { children, eventData, eventGroupsData } = props;
	const cartConfig = useCartStore((s) => s.cartConfig);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, 'Event/Cart/Page');
	const cartState = useCartStore((s) => cartStoreSelectors.getCartState(s));
	const router = useRouter();
	const format = useFormat();
	const currentCartView = useCartStore((s) => s.currentCartView);
	const setCartView = useCartStore((s) => s.setCartView);
	const multiViewProvider = React.useMemo(() => ({ activeView: currentCartView, changeView: setCartView }), [currentCartView, setCartView]);

	const footerRef = React.useRef<HTMLDivElement | null>(null);
	const orgIntegratorConfig = useOrgIntegratorConfig();

	/** format event data */
	const { event, eventGroups } = React.useMemo<{
		event: EventCartPageContextProps['event'];
		eventGroups: EventCartPageProps['eventGroupsData']['eventGroups'];
	}>(() => {
		return {
			event: {
				eventId: eventData.eventId,
				place: eventData.place,
				subtitle: eventData.subtitle,
				logoUrl: eventData.logoUrl,
				receiptLogoUrl: eventData.receiptLogoUrl,
				description: eventData.description,
				namePub: eventData.namePub,
				slug: eventData.slug,
				holderRequired: eventData.holderRequired,
				regionId: eventData.regionId,
				headerImageUrl: eventData.headerImageUrl,
				descriptionMd: eventData.descriptionMd,
				reservationVenue: eventData.reservationVenue,
				saleInfo: cartConfig.saleInfo,
				openInfo: eventData.openInfo,
				locationVenue: isDefined(eventData.locationVenue)
					? {
							...eventData.locationVenue,
							latitude: eventData.locationVenue.locPointLocation[1]!,
							longitude: eventData.locationVenue.locPointLocation[0]!,
						}
					: undefined,
				categories: eventData.categories,
				takesPlaceFrom: new Date(eventData.takesPlaceFrom),
				takesPlaceTo: new Date(eventData.takesPlaceTo),
				currency: constructEventCurrency(eventData.currency),
				extraCurrencies: eventData.currency.extraCurrencies,
				eventColors: constructEventColorConfig(eventData.eventColors),
				organizer: constructOrganizerEntity(eventData.organizer),
				eventKind: eventKindEnum.fromConfig(eventData),
				eventType: eventTypeEnum.get(eventData.typeId),
				ticketCompany: constructCompanyEntity(eventData.ticketCompany),
				productCompany: constructCompanyEntity(eventData.productCompany),
				paymentMethods: eventData.paymentMethods.map(({ paymentMethodId }) => paymentMethodEnumTickets.get(paymentMethodId)),
				bankAccounts: eventData.bankAccounts.map((account) => ({ bankAccountId: account.accountId, currencyId: account.currencyId })),
				extraData: getEventExtraDataParsed(eventData.extraData).withDefaults,
				queueLimit: eventData.queueLimit,
				generateEmptyChips: eventData.generateEmptyChips,
				defaultTicketTypeGroup: eventData.defaultTicketTypeGroup,
			},
			eventGroups: eventGroupsData.eventGroups.map((eventGroup) => ({
				...eventGroup,
				linkUrl: orgIntegratorConfig.getEventGroupUrl({ eventGroupSlug: eventGroup.slug, locale: router.locale }).toString(),
			})),
		};
	}, [cartConfig.saleInfo, eventData, eventGroupsData.eventGroups, orgIntegratorConfig, router.locale]);
	const eventCategoryIds = event.categories.map((category) => category.categoryId);

	/** get container colors */
	const theme = useMantineTheme();
	const containerColors = React.useMemo<EventCartPageContextProps['containerColors']>(() => {
		const lum = getColor(event.eventColors.background, (c) => c.luminosity());
		if (lum > 0.7) return { backgroundColor: hexAlpha('#F7F7F7'), borderColor: hexAlpha(theme.colors.gray[2]) };
		return { backgroundColor: hexAlpha('#F0F0F0'), borderColor: hexAlpha(theme.colors.gray[1]) };
	}, [event.eventColors.background, theme.colors.gray]);

	/** search params for og image */
	const ogImageParams = useEventOgImageParams(event);

	/** sync document body background with the event background color */
	React.useEffect(() => {
		/** only on desktop, ignore mobile since on mobile view (Safari) the background is not visible */
		if (typeof window !== 'undefined' && !window.matchMedia('(max-width: 768px)').matches) {
			document.body.style.backgroundColor = event.eventColors.background;
		}
	}, [event.eventColors.background]);

	/** ticket types API */
	const { ticketTypesApi } = useTicketTypesApi();

	/** maximus products API */
	const { maximusProductsApi } = useMaximusProductsApi();

	/** active org partner codes */
	const orgPartnerCodes = useCartStore((state) => cartStoreSelectors.listOrgPartners(state));

	/** meta offers */
	const ticketTypesOffersJsonLd = useCartStore((state) => {
		const listedTicketTypes = cartStoreSelectors.listTicketTypes(state);
		return [
			{
				'@type': 'AggregateOffer',
				priceCurrency: event.currency.baseCurrency.isoChar,
				lowPrice: listedTicketTypes.reduce((min, ticket) => Math.min(min, ticket.price), Number.MAX_SAFE_INTEGER) / 100,
				highPrice: listedTicketTypes.reduce((max, ticket) => Math.max(max, ticket.price), Number.MIN_SAFE_INTEGER) / 100,
				availability: 'https://schema.org/InStock',
				availabilityEnds: event.saleInfo.saleTo?.toISOString(),
				availabilityStarts: event.saleInfo.saleFrom?.toISOString(),
				validFrom: event.saleInfo.saleFrom?.toISOString(),
				url: orgIntegratorConfig.getEventUrl({ eventSlug: event.slug, organizerSlug: event.organizer.slug, locale: router.locale }).href,
			},
			...listedTicketTypes.map(
				(ticketType): Offer => ({
					'@type': 'Offer',
					name: ticketType.namePub,
					price: ticketType.price / 100,
					priceCurrency: event.currency.baseCurrency.isoChar,
					url: orgIntegratorConfig.getEventUrl({ eventSlug: event.slug, organizerSlug: event.organizer.slug, locale: router.locale }).href,
					availability: ticketType.ticketsAvailable > 0 ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
					inventoryLevel: {
						'@type': 'QuantitativeValue',
						minValue: 0,
						maxValue: ticketType.ticketsAvailable,
					},
				}),
			),
		] satisfies Offer[];
	}, 'deep');

	/** contact details */
	const contactDetails = useCartStore((s) => s.contactDetails);
	const updateContactDetails = useCartStore((s) => s.updateContactDetails);
	const userAccount = useSessionStore((state) => state.account?.account);
	React.useEffect(() => {
		/** sync cart store contactDetails upon session store account state change (e.g., login) */
		if (isDefined(userAccount)) {
			updateContactDetails({ ...contactDetails, ...userAccount, ...userAccount.billingInfo });
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [userAccount]);

	/** TODO: find better solution to custom domains backlinks */
	const backLinkString = React.useMemo(() => {
		const backLink = router.query.backLink?.toString();
		if (backLink === '/' && eventGroups.length > 0) {
			return `/tour/${eventGroups[0]!.slug}`;
		}
		return backLink;
	}, [router.query.backLink, eventGroups]);

	const eventCartPageCtx = React.useMemo<EventCartPageContextProps>(() => {
		return { footerRef, event, containerColors, state: { ...cartState } };
	}, [event, containerColors, cartState, footerRef]);

	return (
		<React.Fragment>
			{/* meta tags */}
			<Head>
				{/* basic SEO meta tags */}
				<title>{t('cartSeo.title', { eventName: event.namePub, context: event.eventType.eventTypeId.toString() })}</title>
				<meta name="description" content={event.description} />

				{/* each page should have its own self-referencing canonical URL (orgIntegrator specific supported) */}
				<link
					rel="canonical"
					href={orgIntegratorConfig.getEventUrl({ eventSlug: event.slug, organizerSlug: event.organizer.slug, locale: undefined }).toString()}
				/>
				{/* hreflang tags for all language versions */}
				{enumToArray(APP_LOCALE).map((locale) => (
					<link
						key={`hreflang-${locale}`}
						rel="alternate"
						hrefLang={locale}
						href={orgIntegratorConfig.getEventUrl({ eventSlug: event.slug, organizerSlug: event.organizer.slug, locale }).toString()}
					/>
				))}
				{/* x-default for language selection page or your default language (Czech in your case) */}
				<link
					rel="alternate"
					hrefLang="x-default"
					href={orgIntegratorConfig.getEventUrl({ eventSlug: event.slug, organizerSlug: event.organizer.slug, locale: APP_LOCALE.CS }).toString()}
				/>

				{/* apple-itunes-app */}
				<meta
					key="apple-itunes-app"
					name="apple-itunes-app"
					content={
						orgPartnerCodes.length > 0
							? `app-id=1595341751, app-argument=${getNebulaMobileDeepLink('ticketCart', { eventId: event.eventId, codes: orgPartnerCodes.map((p) => p.shortCode) })}`
							: `app-id=1595341751, app-argument=${getNebulaMobileDeepLink('eventDetail', { eventId: event.eventId })}`
					}
				/>
				<meta name="apple-mobile-web-app-title" content={process.env.NEXT_PUBLIC_APP_NAME} />
				<meta name="application-name" content={process.env.NEXT_PUBLIC_APP_NAME} />
				<meta name="msapplication-TileColor" content={hexAlpha(event.eventColors.background)} />
				{/* open graph */}
				<meta property="og:title" content={event.extraData.ogTitle ?? event.namePub} />
				<meta property="og:description" content={event.extraData.ogDescription ?? event.description} />
				<meta property="og:type" content="website" />
				<meta property="og:url" content={router.asPath} />
				<meta property="og:locale" content={format.language.code} />
				<meta property="og:site_name" content={process.env.NEXT_PUBLIC_APP_NAME} />
				<meta property="og:image" content={event.extraData.ogImageUrl ?? `/api/og?${ogImageParams.toString()}`} />
				<meta property="og:image:width" content="1200" />
				<meta property="og:image:height" content="630" />
				<meta property="og:image:alt" content={event.namePub} />
				<meta property="og:image:type" content="image/png" />
				{/* twitter */}
				<meta name="twitter:card" content="summary_large_image" />
				{/* favicon */}
				{!isEmpty(event.extraData.faviconUrl) && (
					<React.Fragment>
						<link rel="icon" href={event.extraData.faviconUrl} />
						<link rel="icon" type="image/png" sizes="32x32" href={event.extraData.faviconUrl} />
						<link rel="icon" type="image/png" sizes="16x16" href={event.extraData.faviconUrl} />
						<link rel="apple-touch-icon" sizes="180x180" href={event.extraData.faviconUrl} />
						<link rel="mask-icon" href={event.extraData.faviconUrl} color={hexAlpha(event.eventColors.background)} />
						<link rel="shortcut icon" href={event.extraData.faviconUrl} />
					</React.Fragment>
				)}
			</Head>

			{/* json-ld schema */}
			<JsonLd<EventJsonLd>
				id="event-json-ld"
				data={{
					'@type': eventCategoryIds.includes(EVENT_CATEGORY.FESTIVAL)
						? 'Festival'
						: eventCategoryIds.includes(EVENT_CATEGORY.CHILDREN)
							? 'ChildrensEvent'
							: eventCategoryIds.includes(EVENT_CATEGORY.THEATER)
								? 'TheaterEvent'
								: eventCategoryIds.includes(EVENT_CATEGORY.SPORT)
									? 'SportsEvent'
									: includesSome(eventCategoryIds, EVENT_CATEGORY.FOOD, EVENT_CATEGORY.WINE)
										? 'FoodEvent'
										: includesSome(eventCategoryIds, EVENT_CATEGORY.MUSIC, EVENT_CATEGORY.PARTY, EVENT_CATEGORY.CONCERT)
											? 'MusicEvent'
											: 'Event',
					name: event.namePub,
					alternateName: `${event.namePub} | ${event.subtitle}`,
					startDate: event.takesPlaceFrom.toISOString(),
					endDate: event.takesPlaceTo.toISOString(),
					doorTime: event.openInfo ?? event.takesPlaceFrom.toISOString(),
					duration: formatISODuration(intervalToDuration({ start: event.takesPlaceFrom, end: event.takesPlaceTo })),
					description: event.description,
					url: orgIntegratorConfig.getEventUrl({ eventSlug: event.slug, organizerSlug: event.organizer.slug, locale: router.locale }).href,
					image: [event.extraData.ogImageUrl ?? `/api/og?${ogImageParams.toString()}`],
					location: createJsonLdPlace({
						place: event.place,
						regionId: event.regionId,
						locationVenue: mapDefined(event.locationVenue, (locationVenue) => ({
							namePub: locationVenue.name,
							coordinates: [locationVenue.latitude, locationVenue.longitude],
						})),
					}),
					organizer: createJsonLdOrganizer({ namePub: event.organizer.name, slug: event.organizer.slug }),
					eventStatus: 'https://schema.org/EventScheduled',
					eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
					offers: ticketTypesOffersJsonLd,
					sameAs: event.extraData.links.map((link) => link.href),
					superEvent: eventGroups.map((eventGroup) => ({
						'@type': 'EventSeries',
						name: eventGroup.namePub,
						url: orgIntegratorConfig.getEventGroupUrl({ eventGroupSlug: eventGroup.slug, locale: router.locale }).href,
						organizer: createJsonLdOrganizer({ namePub: event.organizer.name, slug: event.organizer.slug }),
					})),
				}}
			/>

			<MultiView provider={multiViewProvider}>
				{/* content */}
				<EventCartPageContext value={eventCartPageCtx}>
					<AppShell padding={0} header={{ height: 74 }} styles={{ root: { backgroundColor: event.eventColors.background } }}>
						<Header
							logoNode={
								!isEmpty(event.extraData.navbarLogo?.logoUrl) ? (
									<img
										alt={event.namePub}
										src={event.extraData.navbarLogo?.logoUrl}
										style={{ width: event.extraData.navbarLogo?.width, height: event.extraData.navbarLogo?.height ?? 40 }}
									/>
								) : (
									orgIntegratorConfig.logoNode
								)
							}
							backLink={backLinkString || event.extraData.navbarLogo?.backUrl || orgIntegratorConfig.backUrl.href}
						/>
						<EventLayoutAtom
							eventGroups={eventGroups}
							hideOrganizerInCart={event.extraData.hideOrganizerInCart}
							eventCategories={eventCategoryIds}
							eventColors={event.eventColors}
							eventSubtitle={event.subtitle}
							eventTakesPlaceFrom={event.takesPlaceFrom}
							eventTakesPlaceTo={event.takesPlaceTo}
							eventOpenInfo={event.openInfo}
							eventLogoUrl={event.receiptLogoUrl}
							headerImageUrl={event.headerImageUrl}
							eventName={event.namePub}
							eventPlace={event.place}
							eventLocationVenue={event.locationVenue}
							eventRegionId={event.regionId}
							eventDescription={event.description}
							eventDescriptionMd={event.descriptionMd}
							organizerName={event.organizer.name}
							organizerLogoUrl={event.organizer.logoUrl}
							organizerLinkUrl={`/${router.locale}/organizer/${event.organizer.slug}`}
							organizerColors={event.organizer.organizerColors}
							eventLinks={event.extraData.links}
							eventVirtualCurrency={event.currency.virtualCurrency}
							eventCurrencyId={event.currency.baseCurrency.currencyId}
							eventExtraCurrencies={event.extraCurrencies}
							onSitePayment={event.extraData.onSitePayment}
							withReturnableCupsControl={event.extraData.withReturnableCupsControl}
							withZoneControl={event.extraData.withZoneControl}
							ticketCompany={event.ticketCompany}
							productCompany={event.productCompany}
						>
							<EventCartPageContent>{children}</EventCartPageContent>
						</EventLayoutAtom>

						<FooterAtom />
					</AppShell>
				</EventCartPageContext>
			</MultiView>
		</React.Fragment>
	);
};

/**
 * Main cart page view component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
const EventCartPage: PageComponent<typeof getStaticProps> = ({ eventData, eventGroupsData, tracking, __namespaces }) => {
	const queryClient = useQueryClient();
	const sessionAccount = useSessionStore((state) => state.account);
	const orgIntegratorConfig = useOrgIntegratorConfig();
	const config = React.useMemo(() => {
		const extraData = getEventExtraDataParsed(eventData.extraData).withDefaults;
		const cartTicketSaleState = getCartTicketSaleState({
			countdownDate: mapDefined(extraData.countdownDate, (d) => new Date(d)) ?? null,
			saleFrom: mapDefined(eventData.saleInfo?.saleFrom, (d) => new Date(d)) ?? null,
			saleTo: mapDefined(eventData.saleInfo?.saleTo, (d) => new Date(d)) ?? null,
			ticketsSet: eventData.saleInfo?.ticketsSet ?? false,
			hasPaymentMethods: eventData.paymentMethods.length > 0,
		});
		return { extraData, cartTicketSaleState };
	}, [eventData.extraData, eventData.saleInfo, eventData.paymentMethods]);

	const { entryQueueApi, isEntryQueueActive, canContinueFetching } = useEntryQueue({
		isTicketSaleEnabled: config.cartTicketSaleState.isTicketSaleEnabled,
		eventId: eventData.eventId,
		organizerSlug: eventData.organizer.slug,
		queueLimit: eventData.queueLimit,
	});

	const cartConfig = React.useMemo(() => {
		return {
			eventId: eventData.eventId,
			isDemoCart: false,
			currencyId: eventData.currency.baseCurrencyId,
			reqSourceId: orgIntegratorConfig.reqSource,
			isReservationVenue: isDefined(eventData.reservationVenue),
			isEntryQueueActive,
			isEntryQueuePrevented: !canContinueFetching,
			isTicketHolderNameRequired: eventData.holderRequired,
			isTicketEmptyChipsGenerated: eventData.generateEmptyChips ?? false,
			eventTypeId: eventData.typeId,
			saleInfo: {
				ticketsSet: eventData.saleInfo?.ticketsSet ?? false,
				saleFrom: mapDefined(eventData.saleInfo?.saleFrom, (d) => new Date(d)) ?? null,
				saleTo: mapDefined(eventData.saleInfo?.saleTo, (d) => new Date(d)) ?? null,
				countdownDate: mapDefined(config.extraData.countdownDate, (d) => new Date(d)) ?? null,
			},
			importProviders: { goout: config.extraData.gooutImportProvider, nfctron: config.extraData.nfctronImportProvider },
			eventContext: {
				eventGroups: eventGroupsData.eventGroups.map((group) => ({ namePub: group.namePub })),
				eventLocation: eventData.locationVenue?.name,
				eventPlace: eventData.place,
				eventName: eventData.namePub,
			},
		};
	}, [
		eventData.eventId,
		eventData.currency.baseCurrencyId,
		eventData.reservationVenue,
		eventData.holderRequired,
		eventData.generateEmptyChips,
		eventData.typeId,
		eventData.saleInfo?.ticketsSet,
		eventData.saleInfo?.saleFrom,
		eventData.saleInfo?.saleTo,
		eventData.locationVenue?.name,
		eventData.place,
		eventData.namePub,
		orgIntegratorConfig.reqSource,
		isEntryQueueActive,
		canContinueFetching,
		config.extraData.countdownDate,
		config.extraData.gooutImportProvider,
		config.extraData.nfctronImportProvider,
		eventGroupsData.eventGroups,
	]);

	/** debug data */
	useDebug({
		modal: {
			value: [
				['rawEventData', eventData],
				['tracking', tracking],
				['event', eventData],
				['cartConfig', cartConfig],
				['extraData', config.extraData],
				['entryQueueApi', { isEntryQueueActive, canContinueFetching, data: entryQueueApi.data }],
				['i18n.__namespaces', __namespaces],
			],
			actions: (
				<div>
					<Link href={`/event/${eventData.organizer.slug}/${eventData.slug}/demo?secret=${encodeBase64(eventData.eventId)}`} target="_blank">
						<UiButton variant="subtle" leftSection={<LockIcon />}>
							Demo cart
						</UiButton>
					</Link>
				</div>
			),
		},
	});

	return (
		<CartStoreProvider
			initialValue={{
				queryClient,
				cartConfig,
				sessionUserAccount: sessionAccount ?? null,
				paymentMethods: eventData.paymentMethods.map((p) => ({ paymentMethodId: p.paymentMethodId, displayOrder: p.displayOrder })),
				bankAccounts: eventData.bankAccounts.map((a) => ({ bankAccountId: a.accountId, currencyId: a.currencyId })),
			}}
		>
			<CartInner eventData={eventData} eventGroupsData={eventGroupsData} />
		</CartStoreProvider>
	);
};
/**
 * Page display name
 * @type {string}
 */
EventCartPage.displayName = 'Event/Cart/Page';

/**
 * Returns static page props
 * @export
 */
export const getStaticProps = wrapStaticPropsWithI18n<EventCartPageProps, Params & Query>(
	async ({ params, locale }) => {
		try {
			const passApi = createPassApi({ locale });
			const queryClient = new QueryClient();

			/** get event data from API */
			const language = languageEnumTickets.get(locale, 'code');
			const { data: eventData, status } = await passApi.v2Event.getEventBySlugs.request(
				{ eventSlug: params!.event, organizerSlug: params!.organizer },
				{ language: language.languageId },
			);
			if (status === 204) {
				throw new ApiError('Event not found', status);
			}

			const extraData = getEventExtraDataParsed(eventData.extraData).withDefaults;

			/** get event groups data from API */
			const { data: eventGroupsData } = await passApi.v2Event.getEventEventGroups.request({ eventId: eventData.eventId });

			/** get event page data */
			const eventPageData = getEventPageOverride({
				eventData,
				eventGroupsData,
				/** create tracking configuration from extraData config */
				tracking: getEventTrackingOverride(eventData, extraData),
			});

			/** get initial ticket types from API */
			const ticketTypesRes = await passApi.v2Event.getTicketTypesFromEventFull.request(
				{ eventId: eventData.eventId },
				{ filter: undefined, language: language.languageId },
			);
			const ticketTypesConfig = ticketTypesRes.data.map((ticketType) =>
				createTicketTypeConfig(
					{ ...ticketType, saleFrom: null, saleTo: null },
					{
						eventId: eventData.eventId,
						isTicketHolderNameRequired: eventPageData.eventData.holderRequired,
						isTicketEmptyChipsGenerated: eventPageData.eventData.generateEmptyChips ?? false,
					},
				),
			);
			await queryClient.setQueryData(
				cartStoreQueryKeys.getTicketTypesApiQueryKey(eventData.eventId, locale, null),
				{ ticketTypesConfig },
				/** force update upon query load on client */
				{ updatedAt: -1 },
			);

			/** prefetch maximus products query */
			await queryClient.setQueryData(
				cartStoreQueryKeys.getMaximusProductsApiQueryKey(eventData.eventId, locale),
				await getMaximusProductsApiQueryFn({ eventId: eventData.eventId, locale, passApi }),
				/** force update upon query load on client */
				{ updatedAt: -1 },
			);

			/** enable prefetching of venue and categories queries only when there are reservable ticket types */
			const ticketTypes = queryClient.getQueryData<{ ticketTypesConfig: CartStoreTicketTypesSlice.TicketTypeConfig[] }>(
				cartStoreQueryKeys.getTicketTypesApiQueryKey(eventData.eventId, locale, null),
			);
			if (ticketTypes?.ticketTypesConfig.some((ticketType) => ticketType.isReservable)) {
				/** prefetch venue and categories queries */
				await queryClient.setQueryData(
					cartStoreQueryKeys.getVenueApiQueryKey(eventData.eventId, locale),
					await venueApiQueryFn({ eventId: eventData.eventId, passApi }),
					/** force update upon query load on client */
					{ updatedAt: -1 },
				);
				await queryClient.setQueryData(
					cartStoreQueryKeys.getCategoriesApiQueryKey(eventData.eventId, locale),
					await categoriesApiQueryFn({ eventId: eventData.eventId, passApi }),
					/** force update upon query load on client */
					{ updatedAt: -1 },
				);
			}

			return {
				revalidate: makeTime('5 minutes'),
				props: {
					...eventPageData,
					dehydratedQueryClientState: dehydrate(queryClient),
				},
			};
		} catch (e) {
			console.error(`[${EventCartPage.displayName}] Could not fetch event data or event groups data`, { params, e });
			/** ignore basic API errors */
			if (matchesApiError(e, [204, 404])) return { notFound: true };
			/** capture error to Sentry */
			Sentry.captureException(e, { tags: { getStaticProps: EventCartPage.displayName, ...params } });
			/** re-throw any other error to keep the previous generated static data */
			/** @see https://nextjs.org/docs/canary/pages/building-your-application/data-fetching/incremental-static-regeneration#handling-uncaught-exceptions */
			throw e;
		}
	},
	{ pathname: '/event/[organizer]/[event]/cart' },
);

/**
 * Returns static paths props
 * @export
 */
export const getStaticPaths: GetStaticPaths<Params> = async () => {
	/** skip page generation to enable faster builds, but longer initial loads */
	if (process.env.SKIP_BUILD_STATIC_GENERATION === 'true') {
		return { paths: [], fallback: 'blocking' };
	}

	try {
		/** call API to get all event slugs */
		const passApi = createPassApi({});
		const { data: events } = await passApi.v2Event.findAllVisibleEventsSlugs.request();

		return {
			paths: getStaticPathsWithLocales(events.map(({ eventSlug, organizerSlug }) => ({ params: { event: eventSlug, organizer: organizerSlug } }))),
			fallback: 'blocking',
		};
	} catch (e) {
		/** capture error to Sentry */
		Sentry.captureException(e, { tags: { getStaticPaths: EventCartPage.displayName } });
		return { paths: [], fallback: 'blocking' };
	}
};

/**
 * Page props type
 */
export type EventCartPageProps = PageWithCommonProps<{
	eventData: UseInitCartPageData;
	eventGroupsData: GetEventEventGroupsResultDto;
}>;

/**
 * Page params type
 * @private
 */
type Params = PageParams<'organizer' | 'event'>;

/**
 * Page query type
 * @private
 */
type Query = PageQuery<never>;

/**
 * Default page export
 * @default
 */
export default EventCartPage;
